// General Mixpanel event, metadata is not mandatory field
import { MockService, MockServiceType, SV } from 'types/rest/mockServiceTypes';
import { BlueprintVisibility } from 'types/rest/assetCatalogTypes';
import { isBlazeVseMockService } from 'utils/mockServiceUtils';
import { AssetType } from '../store/certificates/certificatesTypes';
import { BlueprintViewMode } from '../store/blueprints/blueprintsTypes';

export interface MixPanelEventType {
  event: string;
  metadata?: any;
}

// Status of the Request
export enum OperationStatus {
  SUCCESS,
  ERROR,
}

export const BZCT_EVENT_PREFIX = 'BZCT-';

/* Virtual Services */
export const DEPLOY_MOCK_SERVICE = (mockService: MockService): MixPanelEventType => {
  return {
    event: BZCT_EVENT_PREFIX + 'DEPLOY-MOCK-SERVICE',
    metadata: createMockServiceMeta(mockService),
  };
};

export const CREATE_MOCK_SERVICE = (origin: any): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'CREATE-MOCK-SERVICE',
  metadata: {
    origin: origin,
  },
});

export const RECONFIURE_MOCK_SERVICE = (mockService: MockService): MixPanelEventType => {
  return {
    event: BZCT_EVENT_PREFIX + 'RECONFIGURE-MOCK-SERVICE',
    metadata: createMockServiceMeta(mockService),
  };
};

export const FILTER_MOCK_SERVICES = (searchTerm: string): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'MOCK-SEARCH',
  metadata: {
    filterText: searchTerm,
  },
});

export const DELETE_MOCK_SERVICE = (type: MockServiceType): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'DELETE-MOCK-SERVICE',
  metadata: {
    mockServiceType: type,
  },
});

export const STOP_MOCK_SERVICE = (type: MockServiceType): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'STOP-MOCK-SERVICE',
  metadata: {
    mockServiceType: type,
  },
});

export const UPDATE_MOCK_SERVICE = (type: MockServiceType): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'UPDATE-MOCK-SERVICE',
  metadata: {
    mockServiceType: type,
  },
});

export const COPY_ENDPOINT_MOCK_SERVICE = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'COPY-ENDPOINT-MOCK-SERVICE',
});

/* Drozone */

/**
 * Tracked when user drops file to the dropzone.
 *
 * @param fileType type of the file being processed
 * @param verificationStatus status of the action
 * @param transactionsPreprocessed how many transaction were valid
 * @param context which dropzone was used
 * @constructor
 */
export const DROPZONE_FILE_DROPPED = (
  fileType: string,
  verificationStatus: OperationStatus,
  transactionsPreprocessed: number,
  context?: string,
): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'DROPZONE-FILE-DROPPED',
  metadata: {
    fileType: fileType,
    verificationStatus: OperationStatus[verificationStatus],
    transactionsPreprocessed: transactionsPreprocessed,
    context: context,
  },
});

/**
 *
 * @param transactionsCreated number of transactions created.
 *
 * @param numberOfTags number of tags used
 * @param creationStatus Success / Failure
 * @param error message with error details
 * @constructor
 */
export const DROPZONE_CREATE_TRANSACTIONS = (
  transactionsCreated: number,
  numberOfTags: number,
  creationStatus: OperationStatus,
  error?: string,
): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'CREATE-TRANSACTIONS',
  metadata: {
    transactionsCreated: transactionsCreated,
    numberOfTags: numberOfTags,
    creationStatus: OperationStatus[creationStatus],
    error: error,
  },
});

/**
 * Open Top Menu tab item.
 * @constructor
 */
export const OPEN_TOP_MENU_TAB = (tabName: string): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'OPEN-TOP-MENU-TAB',
  metadata: {
    tab: tabName,
  },
});

/**
 * Create blank transaction. It does not have to be saved.
 * @constructor
 */
export const CREATE_BLANK_TRANSACTION = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'CREATE-BLANK-TRANSACTION',
});

/**
 * Close blank transaction.
 * @constructor
 */
export const CLOSE_BLANK_TRANSACTION = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'CLOSE-BLANK-TRANSACTION',
});

/**
 * Modify transaction.
 * @constructor
 */
export const UPDATE_TRANSACTION = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'UPDATE-TRANSACTION',
});

/**
 * Create transaction and persist it to db.
 * @constructor
 */
export const CREATE_TRANSACTION = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'CREATE-TRANSACTION',
});

/**
 * Modify transaction
 * @constructor
 */
export const DELETE_TRANSACTION = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'DELETE-TRANSACTION',
});

/**
 * Clone transaction
 * @constructor
 */
export const CLONE_TRANSACTION = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'CLONE-TRANSACTION',
});

/**
 * Clone Configuration
 * @constructor
 */
export const CLONE_CONFIGURATION = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'CLONE-CONFIGURATION',
});

/**
 * Change of the workspace.
 * @constructor
 */
export const CHANGE_TRANSACTION_SERVICE = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'CHANGE-TRANSACTION-SERVICE',
});

/**
 * Bulk operation for transactions
 * @param type operation type (tag, delete)
 * @param count how many transactions affected by the operation
 * @constructor
 */
export const TRANSACTION_BULK_OPERATION = (type: string, count: number): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'TRANSACTION-BULK-OPERATION',
  metadata: {
    type: type,
    count: count,
  },
});

/**
 * Bulk operation for transactions
 * @param type operation type (tag, delete)
 * @param count how many transactions affected by the operation
 * @constructor
 */
export const MAR_FILES_BULK_OPERATION = (type: string, count: number): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'MAR_FILES_BULK_OPERATION',
  metadata: {
    type: type,
    count: count,
  },
});

/**
 * Bulk operation for transactions
 * @param type operation type (tag, delete)
 * @param count how many transactions affected by the operation
 * @constructor
 */
export const ASSET_FILES_BULK_OPERATION = (type: string, count: number): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'ASSET_FILES_BULK_OPERATION',
  metadata: {
    type: type,
    count: count,
  },
});

/**
 * Data Settings tab opened
 * @param tabName in the URL of the browser
 * @constructor
 */
export const DATA_SETTINGS_OPEN = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'DATA-SETTINGS-OPEN',
});

/**
 * Deployed data is downloaded by the user.
 * @constructor
 */
export const DOWNLOAD_BLAZE_DATA = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'DOWNLOAD-BLAZE-DATA',
});

/**
 * Opens blaze data panel from virtual service data settings.
 * @constructor
 */
export const OPEN_BLAZE_DATA_PANEL_FROM_MOCK_SERVICE = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'OPEN-BLAZE-DATA-PANEL-FROM-MOCK-SERVICE',
});

/**
 * If is a difference between deployed model and current model user applies
 * changes and create virtual service draft.
 * @constructor
 */
export const APPLY_CHANGES_FROM_BLAZE_DATA_MODEL = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'APPLY-CHANGES-FROM-BLAZE-DATA-MODEL',
});

/**
 * User is overriding blaze data iteration settings in the virtual service.
 * @constructor
 */
export const DATA_SETTINGS_CREATE_ITERATION_OVERRIDE = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'DATA-SETTINGS-CREATE-ITERATION-OVERRIDE',
});

/**
 * Change of the React Router location.
 * @param tabName in the URL of the browser
 * @constructor
 */
export const TAB_CHANGED = (tabName: string): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'TAB-CHANGED',
  metadata: {
    tabName: tabName,
  },
});

/**
 * Create service.
 * @constructor
 */
export const CREATE_SERVICE = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'CREATE-SERVICE',
});

/**
 * Create service from template.
 * @constructor
 */
export const CREATE_SERVICE_FROM_TEMPLATE = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'CREATE-SERVICE-FROM-TEMPLATE',
});

/**
 * Create service snapshot
 * @constructor
 */
export const CREATE_SERVICE_SNAPSHOT = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'CREATE-SERVICE-SNAPSHOT',
});

/**
 * Create blank template. It's the user clicking on the plus button.
 * @constructor
 */
export const CREATE_BLANK_TEMPLATE = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'CREATE-BLANK-TEMPLATE',
});

/**
 * Update template.
 * @constructor
 */
export const UPDATE_TEMPLATE = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'UPDATE-TEMPLATE',
});

/**
 * Create template
 * @constructor
 */
export const CREATE_TEMPLATE = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'CREATE-TEMPLATE',
});

export const DELETE_TEMPLATE = (isBlank: boolean): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'DELETE-TEMPLATE',
  metadata: {
    isBlank: isBlank,
  },
});

/**
 * Change of the workspace.
 * @constructor
 */
export const WORKSPACE_CHANGED = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'WORKSPACE-CHANGED',
});

/**
 * Account switched via the account selector.
 * @constructor
 */
export const ACCOUNT_CHANGED = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'ACCOUNT-CHANGED',
});

/**
 * Modify Mar
 * @constructor
 */
export const DELETE_ASSET = (
  certName: string,
  creationStatus: OperationStatus,
  assetType: AssetType,
  error?: string,
): MixPanelEventType => {
  const assetEvent = `DELETE-${assetType}`;
  return {
    event: BZCT_EVENT_PREFIX + assetEvent,
    metadata: {
      creationStatus: OperationStatus[creationStatus],
      certName: certName,
      error: error,
    },
  };
};

/**
 * Modify Mar
 * @constructor
 */
export const DELETE_MAR = (marName: string, creationStatus: OperationStatus, error?: string): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'DELETE-MAR',
  metadata: {
    creationStatus: OperationStatus[creationStatus],
    marName: marName,
    error: error,
  },
});

/**
 * Download Mar
 * @constructor
 */
export const DOWNLOAD_MAR = (marName: string, creationStatus: OperationStatus, error?: string): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'DOWNLOAD-MAR',
  metadata: {
    creationStatus: OperationStatus[creationStatus],
    marName: marName,
    error: error,
  },
});

export const CHANGE_MOCK_SERVICE_LOCATION = (msStatus: string): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'CHANGE-MOCK-SERVICE-LOCATION',
  metadata: {
    mockServiceStatus: msStatus,
  },
});

/**
 * Creates Metadata information for Mixpanel create / re-configure events.
 * @param mockService
 */
const createMockServiceMeta = (mockService: MockService) => {
  let meta: any = {
    type: mockService.type,
  };

  // If we are working with SV virtual service, add also subtype to meta data so we know
  // whether ms is deployed via bridge or to blaze vse
  if (mockService.type === 'SV') {
    const deploymentTarget = isBlazeVseMockService(mockService) ? 'BLAZE_VSE' : 'BRIDGE';
    meta = { ...meta, deploymentTarget: deploymentTarget };
  }

  return meta;
};

/**
 * Bulk operation for mocks
 * @param type operation type (tag, delete)
 * @param count how many mocks affected by the operation
 * @constructor
 */
export const MOCK_SERVICE_BULK_OPERATION = (type: string, count: number): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'MOCK-SERVICE-BULK-OPERATION',
  metadata: {
    type: type,
    count: count,
  },
});

/**
 * Open analytics of virtual service.
 * @Param mockServiceId virtual service Id
 * @constructor
 */
export const OPEN_ANALYTICS_MOCK_SERVICE = (mockServiceId: number): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'OPEN_ANALYTICS_MOCK_SERVICE',
  metadata: {
    mockServiceId: mockServiceId,
  },
});

/**
 * Open analytics form mockservice screen.
 * @param mockServiceId virtual service id
 * @param type virtual service type
 * @constructor
 */
export const OPEN_ANALYTICS_FROM_MS_SCREEN = (mockServiceId: number, type: string): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'OPEN_ANALYTICS_FROM_MS_SCREEN',
  metadata: {
    mockServiceId: mockServiceId,
    type: type,
  },
});

/**
 * Open analytics subview: inspection or reporting view.
 * @Param subviewType of analytics
 * @constructor
 */
export const OPEN_ANALYTICS_SUBVIEW = (subviewType: string): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'OPEN_ANALYTICS_SUBVIEW',
  metadata: {
    subview: subviewType,
  },
});

/**
 * Go back from analytics to virtual service
 * @Param mockServiceId virtual service id
 * @constructor
 */
export const GO_TO_MOCK_SERVICE_FROM_ANALYTICS = (mockServiceId: number): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'GO_TO_MOCK_SERVICE_FROM_ANALYTICS',
  metadata: {
    mockServiceId: mockServiceId,
  },
});

/**
 * refresh analytics to virtual service
 * @Param tab to refresh
 * @constructor
 */
export const REFRESH_INSPECTION_VIEW = (tab: any): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'REFRESH_INSPECTION_VIEW',
  metadata: {
    tab: tab,
  },
});

/**
 * refresh analytics to virtual service
 * @Param tab to refresh
 * @constructor
 */
export const REFRESH_WEBHOOK_VIEW = (tab: any): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'REFRESH_WEBHOOK_VIEW',
  metadata: {
    tab: tab,
  },
});

/**
 * refresh analytics to virtual service
 * @Param tab to refresh
 * @constructor
 */
export const REFRESH_HTTP_CALL_VIEW = (tab: any): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'REFRESH_HTTP_CALL_VIEW',
  metadata: {
    tab: tab,
  },
});

/**
 * refresh analytics to virtual service
 * @Param tab to refresh
 * @constructor
 */
export const REFRESH_STATE_CHANGES_VIEW = (tab: any): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'REFRESH_STATE_CHANGES_VIEW',
  metadata: {
    tab: tab,
  },
});

/**
 * refresh results from table
 * @constructor
 */
export const SEARCH_FILTER_RESULTS = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'SEARCH_FILTER_RESULTS',
});

/**
 * refresh data for reporting graphs
 * @constructor
 */
export const REFRESH_REPORTING_VIEW = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'REFRESH_REPORTING_VIEW',
});

/**
 * selected report type to show in graph
 * @Param reportType selected
 * @constructor
 */
export const REPORT_SELECTED_TYPE = (reportType: string): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'REPORT_SELECTED_TYPE',
  metadata: {
    reportType: reportType,
  },
});

/**
 * time range changed
 * @constructor
 */
export const TIME_RANGE_CHANGED = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'TIME_RANGE_CHANGED',
});

/**
 * fixed time range changed
 * @Param timeRange selected
 * @constructor
 */
export const FIXED_TIME_RANGE_CHANGED = (timeRange: string): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'PRESET_TIME_RANGE_CHANGED',
  metadata: {
    timeRangeSelected: timeRange,
  },
});

/**
 * aggregation changed
 * @Param aggregation selected
 * @constructor
 */
export const AGGREGATION_CHANGED = (aggregation: string): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'AGGREGATION_CHANGED',
  metadata: {
    aggregation: aggregation,
  },
});

/**
 * copy to clipboard event
 * @constructor
 */
export const COPY_TO_CLIPBOARD = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'COPY_TO_CLIPBOARD',
});

/**
 * analytics tab closed
 * @Param tabId id of analytic tab closed
 * @constructor
 */
export const ANALYTICS_TAB_CLOSED = (tabId: string): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'ANALYTICS_TAB_CLOSED',
  metadata: {
    tabId: tabId,
  },
});

/**
 * Wizard is opened by user.
 * @constructor
 */
export const WIZARD_OPEN = (location: 'Learn More Page' | 'Virtual Service Page'): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'WIZARD_OPEN',
  metadata: {
    location: location,
  },
});

/**
 * Wizard is closed by user.
 * @constructor
 */
export const WIZARD_CLOSE = (stepNumber: number): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'WIZARD_CLOSE',
  metadata: {
    stepNumber: stepNumber,
  },
});

/**
 * Wizard finished.
 * @constructor
 */
export const WIZARD_FINISHED = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'WIZARD_FINISHED',
});

/**
 * requested detail tab
 * @Param type of requested detail tab
 * @constructor
 */
export const SELECTED_REQUEST_DETAIL_TAB = (type: string): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'SELECTED_REQUEST_DETAIL_TAB',
  metadata: {
    requestDetailTab: type,
  },
});

/**
 * httpCall detail tab
 * @Param type of requested detail tab
 * @constructor
 */
export const SELECTED_HTTP_CALLS_DETAIL_TAB = (type: string): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'SELECTED_HTTP_CALLS_DETAIL_TAB',
  metadata: {
    httpCallsDetailTab: type,
  },
});

/**
 * webhook detail tab
 * @Param type of requested detail tab
 * @constructor
 */
export const SELECTED_WEBHOOK_DETAIL_TAB = (type: string): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'SELECTED_WEBHOOK_DETAIL_TAB',
  metadata: {
    webhookDetailTab: type,
  },
});

/**
 * visiting learn more very fist time user.
 *
 * @param context which dropzone was used
 * @constructor
 */
export const FIRST_TIME_VISIT_USER = (context: string): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'FIRST-TIME-VISIT-USER',
  metadata: {
    context: context,
  },
});

/**
 * Click on the bzm header subscription button.
 */
export const BZM_HEADER_SUBSCRIPTION_BTN_CLICK = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'BZM_HEADER_SUBSCRIPTION_BTN_CLICK',
});

/**
 * Click on the bzm Logo in the header.
 */
export const BZM_HEADER_LOGO_CLICK = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'BZM_HEADER_LOGO_CLICK',
});

/**
 * Open Virtual Service sub Tab
 */
export const OPEN_MOCK_SERVICE_TAB = (tab: string): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'OPEN-MOCK-SERVICE-TAB',
  metadata: {
    tab: tab,
  },
});

/**
 * Create New Group
 */
export const CREATE_MOCK_SERVICE_GROUP = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'CREATE-MOCK-SERVICE-GROUP',
});

/**
 * Assign Virtual Service to Group - open Modal
 */
export const ASSIGN_MOCK_SERVICE_TO_GROUP_OPEN_MODAL = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'ASSIGN-MOCK-SERVICE-TO-GROUP-OPEN-MODAL',
});

/**
 * Assign Virtual Service to Group
 */
export const ASSIGN_MOCK_SERVICE_TO_GROUP = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'ASSIGN-MOCK-SERVICE-TO-GROUP',
});

/**
 * Enable SSL Configuration for Group
 */
export const UPDATE_SSL_CONFIGURATION_FOR_GROUP = (sslConfig: 'No Ssl' | 'One Way SSL'): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'ENABLE-SSL-CONFIGURATION-FOR-GROUP',
  metadata: {
    sslConfig: sslConfig,
  },
});

/**
 * Virtual Service - Show More
 */
export const MOCK_SERVICE_SHOW_MORE = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'MOCK-SERVICE-SHOW-MORE',
});

/**
 * Deassign Virtual Service from Group - Single
 */
export const DEASSIGN_MOCK_SERVICE_FROM_GROUP_SINGLE = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'DEASSIGN-MOCK-SERVICE-FROM-GROUP-SINGLE',
});

/**
 * Deassign Virtual Service from Group - Bulk
 */
export const DEASSIGN_MOCK_SERVICE_FROM_GROUP_BULK = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'DEASSIGN-MOCK-SERVICE-FROM-GROUP-BULK',
});

/**
 * Copy Full Virtual Service Endpoint (within a groups)
 */
export const COPY_FULL_MOCK_SERVICE_ENDPOINT_WITHIN_GROUP = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'COPY-FULL-MOCK-SERVICE-ENDPOINT-WITHIN-GROUP',
});

/**
 * Open Virtual Service Base Path popover
 */
export const OPEN_MOCK_SERVICE_BASE_PATH_POPOVER = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'OPEN-MOCK-SERVICE-BASE-PATH-POPOVER',
});

/**
 * Virtual Service - apply configuration
 */
export const MOCK_SERVICE_APPLY_CONFIGURATION = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'MOCK-SERVICE-APPLY-CONFIGURATION',
});

/**
 * Virtual Service - apply Secret Vault
 */
export const MOCK_SERVICE_APPLY_SECRET_VAULT = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'MOCK-SERVICE-APPLY-SECRET_VAULT',
});

/**
 * Run Virtual Service through group view
 */
export const RUN_MOCK_SERVICE_THROUGH_GROUP_VIEW = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'RUN-MOCK-SERVICE-THROUGH-GROUP-VIEW',
});

/**
 * Stop Virtual Service through group
 */
export const STOP_MOCK_SERVICE_THROUGH_GROUP = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'STOP-MOCK-SERVICE-THROUGH-GROUP',
});

/**
 * Start Virtual Service Group
 */
export const START_MOCK_SERVICE_GROUP = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'START-MOCK-SERVICE-GROUP',
});

/**
 * Stop Virtual Service Group
 */
export const STOP_MOCK_SERVICE_GROUP = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'STOP-MOCK-SERVICE-GROUP',
});

/**
 * Reconfigure Virtual Service Group
 */
export const RECONFIGURE_MOCK_SERVICE_GROUP = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'RECONFIGURE-MOCK-SERVICE-GROUP',
});

/**
 * Export Service to a zip file.
 */
export const EXPORT_SERVICE_TO_ZIP = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'EXPORT-SERVICE-TO-ZIP',
});

/**
 * Import Service from a zip file.
 */
export const IMPORT_SERVICE_FROM_ZIP = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'IMPORT-SERVICE-FROM-ZIP',
});

/**
 * Modal window with transaction recovery open.
 */
export const TXN_RECOVERY_MODAL_OPEN = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'TXN-RECOVERY-MODAL-OPEN',
});

/**
 * Transaction recovery process triggered.
 */
export const TXN_RECOVERY_TRIGGERED = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'TXN-RECOVERY-TRIGGERED',
});

/**
 * Transaction version history tab viewed.
 */
export const VIEW_TRANSACTION_VERSION_HISTORY = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'VIEW-TRANSACTION-VERSION-HISTORY',
});

/**
 * A past transaction version opened in the read-only viewer.
 * @param versionNumber the version number that was viewed
 */
export const VIEW_TRANSACTION_VERSION = (versionNumber: number): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'VIEW-TRANSACTION-VERSION',
  metadata: {
    versionNumber: versionNumber,
  },
});

/**
 * Diff between the current and a past transaction version opened.
 * @param versionNumber the past version being compared against the current one
 */
export const VIEW_TRANSACTION_VERSION_DIFF = (versionNumber: number): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'VIEW-TRANSACTION-VERSION-DIFF',
  metadata: {
    versionNumber: versionNumber,
  },
});

/**
 * A past transaction version restored as the current one.
 * @param versionNumber the version number that was restored
 */
export const RESTORE_TRANSACTION_VERSION = (versionNumber: number): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'RESTORE-TRANSACTION-VERSION',
  metadata: {
    versionNumber: versionNumber,
  },
});

/**
 * Create Recording.
 * @constructor
 */
export const CREATE_RECORDING = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'CREATE-RECORDING',
});

/**
 * Modify Recording.
 * @constructor
 */
export const UPDATE_RECORDING = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'UPDATE-RECORDING',
});

/**
 * Delete Recording.
 * @constructor
 */
export const DELETE_RECORDING = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'DELETE-RECORDING',
});

/**
 * Bulk operation for Recording.
 * @param type operation type (tag, delete)
 * @param count how many recordings affected by the operation
 * @constructor
 */
export const RECORDING_BULK_OPERATION = (type: string, count: number): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'RECORDING-BULK-OPERATION',
  metadata: {
    type: type,
    count: count,
  },
});

/**
 * Create Recording Messages.
 * @constructor
 */
export const CREATE_RECORDING_MESSAGES = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'CREATE-RECORDING-MESSAGES',
});

/**
 * Modify Recording Messages.
 * @constructor
 */
export const UPDATE_RECORDING_MESSAGE = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'UPDATE-RECORDING-MESSAGE',
});

/**
 * Delete Recording Messages
 * @constructor
 */
export const DELETE_RECORDING_MESSAGE = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'DELETE-RECORDING-MESSAGE',
});

/**
 * Bulk operation for Recording Messages
 * @param type operation type (tag, delete)
 * @param count how many recording messages affected by the operation
 * @constructor
 */
export const RECORDING_MESSAGE_BULK_OPERATION = (type: string, count: number): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'RECORDING-MESSAGE-BULK-OPERATION',
  metadata: {
    type: type,
    count: count,
  },
});

/* AI Assistant */

/**
 * AI Assistant modal was opened by the user.
 * @param fieldType the field that triggered the assistant (e.g. "url", "header", "body")
 */
export const AI_ASSISTANT_OPENED = (fieldType: string): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'AI-ASSISTANT-OPENED',
  metadata: {
    fieldType: fieldType,
  },
});

/**
 * User sent a question to the AI Assistant.
 * @param fieldType the field context in which the question was asked
 */
export const AI_ASSISTANT_QUESTION_SENT = (fieldType: string): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'AI-ASSISTANT-QUESTION-SENT',
  metadata: {
    fieldType: fieldType,
  },
});

/**
 * User applied an AI suggestion via Append, Override, or Copy.
 * @param action which button the user clicked
 * @param fieldType the field context in which the suggestion was used
 */
export const AI_ASSISTANT_SUGGESTION_USED = (
  action: 'append' | 'override' | 'replace-selection' | 'copy',
  fieldType: string,
): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'AI-ASSISTANT-SUGGESTION-USED',
  metadata: {
    action: action,
    fieldType: fieldType,
  },
});

/**
 * AI Assistant request was rejected due to rate limiting (HTTP 429).
 * @param fieldType the field context in which the limit was hit
 */
export const AI_ASSISTANT_RATE_LIMIT_HIT = (fieldType: string): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'AI-ASSISTANT-RATE-LIMIT-HIT',
  metadata: {
    fieldType: fieldType,
  },
});

/**
 * Wrong assets found when loading data model — dependencies whose names don't follow the
 * expected `-{serviceId}` naming convention.
 * @param serviceId ID of the service
 * @param wrongAssetIds IDs of the non-conforming dependency assets
 * @constructor
 */
export const DATA_MODEL_WRONG_ASSETS = (serviceId: number, wrongAssetIds: string[]): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'DATA-MODEL-WRONG-ASSETS',
  metadata: {
    serviceId,
    wrongAssetIds,
  },
});

/**
 * Exception when loading data model for service.
 * @param serviceId ID of the service
 * @param forceReload whether the reload was forced
 * @param createLink whether a link was created
 * @param errorMessage error message
 * @constructor
 */
export const DATA_MODEL_LOAD_EXCEPTION = (
  serviceId: number,
  forceReload: boolean,
  createLink: boolean,
  errorMessage: string,
): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'DATA-MODEL-LOAD-EXCEPTION',
  metadata: {
    serviceId,
    forceReload,
    createLink,
    errorMessage,
  },
});

/**
 * Data model recreation after asset not found.
 * @param serviceId ID of the service
 * @param dataModelId ID of the data model (if available)
 * @param dataModelTitle title of the data model (if available)
 * @constructor
 */
export const DATA_MODEL_RECREATED = (
  serviceId: number,
  dataModelId?: string,
  dataModelTitle?: string,
): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'DATA-MODEL-RECREATED',
  metadata: {
    serviceId,
    dataModelId,
    dataModelTitle,
  },
});

/* Blueprints */

/**
 * Tracked when the blueprints list page is opened.
 * @constructor
 */
export const BLUEPRINTS_VIEWED = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'BLUEPRINTS-VIEWED',
});

/**
 * Tracked when a user opens a blueprint's detail view.
 * @param viewMode the list view (GRID/LIST) the detail was opened from
 * @constructor
 */
export const BLUEPRINT_DETAIL_OPENED = (viewMode: BlueprintViewMode): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'BLUEPRINT-DETAIL-OPENED',
  metadata: {
    viewMode,
  },
});

/**
 * Tracked when a user toggles the blueprints list view between grid and list.
 * @param viewMode the newly selected view mode
 * @constructor
 */
export const BLUEPRINT_VIEW_MODE_CHANGED = (viewMode: BlueprintViewMode): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'BLUEPRINT-VIEW-MODE-CHANGED',
  metadata: {
    viewMode,
  },
});

/**
 * Tracked when a user creates a blueprint.
 * @param transactionCount number of transactions snapshotted into the blueprint
 * @param visibility PRIVATE or ACCOUNT
 * @constructor
 */
export const BLUEPRINT_CREATED = (transactionCount: number, visibility: BlueprintVisibility): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'BLUEPRINT-CREATED',
  metadata: {
    transactionCount,
    visibility,
  },
});

/**
 * Tracked when a user adds a blueprint to the catalog (apply).
 * @param targetType whether it targets a new or existing service
 * @param skipBlazeData whether BlazeData is ignored on apply
 * @constructor
 */
export const BLUEPRINT_APPLIED = (targetType: 'new' | 'existing', skipBlazeData: boolean): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'BLUEPRINT-APPLIED',
  metadata: {
    targetType,
    skipBlazeData,
  },
});

/**
 * Tracked when a user saves changes to a blueprint.
 * @constructor
 */
export const BLUEPRINT_UPDATED = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'BLUEPRINT-UPDATED',
});

/**
 * Tracked when a user deletes a blueprint (after confirmation).
 * @constructor
 */
export const BLUEPRINT_DELETED = (): MixPanelEventType => ({
  event: BZCT_EVENT_PREFIX + 'BLUEPRINT-DELETED',
});
